import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlayManager } from '../../contexts/OverlayContext';
import CircleArrowButton from '../CircleArrowButton/CircleArrowButton';
import UnifiedNavigation from '../Navigation/UnifiedNavigation';
import './ResortNavigation.css';

interface Resort {
  name: string;
  path?: string;
  isComingSoon?: boolean;
}

interface ResortSection {
  country: string;
  resorts: Resort[];
}

interface ResortNavigationProps {
  onClose: () => void;
}

const ResortNavigation: React.FC<ResortNavigationProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { toggleMenu } = useOverlayManager();
  const [hoveredResort, setHoveredResort] = useState<string | null>(null);

  // Resort data
  const resortSections: ResortSection[] = [
    {
      country: 'Chile',
      resorts: [
        { name: 'Patagonia', path: '/patagonia/' },
        { name: 'Atacama', path: '/atacama/' }
      ]
    },
    {
      country: 'Brazil',
      resorts: [
        { name: 'Santa Catarina', path: '/santa-catarina/' }
      ]
    },
    {
      country: 'Argentina',
      resorts: [
        { name: 'Mendoza', path: '/mendoza/' },
        { name: 'Iguazu', path: '/iguazu/' }
      ]
    },
    // {
    //   country: 'Coming Soon',
    //   resorts: [
    //     { name: 'Santa Marta', isComingSoon: true },
    //     { name: 'Magallanes', isComingSoon: true }
    //   ]
    // }
  ];

  // Resort mappings with correct images and colors
  const resortMappings: { [key: string]: { image: string; color: string; textTheme: 'light' | 'dark' } } = {
    patagonia: {
      image: '/assets/images/our-story/3db34498071a7adba733deffb8c9280d3f1f26fc.png',
      color: '#c0c6c8', // Light gray
      textTheme: 'dark' // Dark text on light background
    },
    atacama: {
      image: 'https://images.ctfassets.net/hz1mviiqoqof/4zhflu510mUoV6q9DK1TML/6b1bafc1c49fd38fc8e6b71247362a9c/destinationsAtacama.png',
      color: '#7B6569', // Dark mauve
      textTheme: 'light' // White text on dark background
    },
    iguazu: {
      image: '/assets/resort-iguazu-hover-bg.png',
      color: '#3e5b58', // Dark teal
      textTheme: 'light' // White text on dark background
    },
    mendoza: {
      image: '/assets/resort-mendoza-hover-bg.jpg',
      color: '#502630', // Dark burgundy
      textTheme: 'light' // White text on dark background
    },
    'santa catarina': {
      image: '/assets/resort-santa-catarina-hover-bg.jpg',
      color: '#009485', // Turquoise
      textTheme: 'light' // White text on dark background
    },
    default: {
      image: '/assets/menu-default-bg.jpg',
      color: '#f3f3f3', // Off-white
      textTheme: 'dark' // Dark text on light background
    }
  };

  const handleResortClick = (path?: string) => {
    if (path) {
      navigate(path);
      onClose();
      toggleMenu();
    }
  };

  const getCurrentMapping = () => {
    if (!hoveredResort) return resortMappings.default;
    const key = hoveredResort.toLowerCase();
    return resortMappings[key] || resortMappings.default;
  };

  const currentMapping = getCurrentMapping();

  return (
    <div
      className={`resort-navigation open ${currentMapping.textTheme === 'light' ? 'resort-navigation--light-text' : ''}`}
      style={{ backgroundColor: currentMapping.color }}
    >
      {/* Navigation Bar */}
      <UnifiedNavigation
        variant="light"
        forceWhiteAssets={currentMapping.textTheme === 'light'}
      />

      {/* White background to cover menu content */}
      <div className="resort-nav-white-bg" style={{ backgroundColor: currentMapping.color }} />

      {/* Default background image */}
      <div
        className="resort-nav-image-container"
        style={{
          backgroundImage: `url('${resortMappings.default.image}')`,
          opacity: hoveredResort ? 0 : 1
        }}
      />

      {/* Hover background image */}
      {hoveredResort && (
        <div
          className="resort-nav-image-container resort-nav-image-hover"
          style={{
            backgroundImage: `url('${currentMapping.image}')`,
            opacity: 1
          }}
        />
      )}

      {/* Content */}
      <div className="resort-nav-content">
        {/* Header */}
        <div className="resort-nav-header">
          <button className="resort-nav-back" onClick={onClose}>
            <CircleArrowButton />
            <h2>Our Lodges</h2>
          </button>
        </div>

        {/* Resort Grid */}
        <div className="resort-grid">
          {resortSections.map(section => (
            <div key={section.country} className="resort-section">
              <div className="country-label">{section.country}</div>
              <div className="resort-list">
                {section.resorts.map(resort => (
                  <button
                    key={resort.name}
                    className={`resort-item ${resort.isComingSoon ? 'coming-soon' : ''}`}
                    onClick={() => handleResortClick(resort.path)}
                    disabled={resort.isComingSoon}
                    onMouseEnter={() => !resort.isComingSoon && setHoveredResort(resort.name)}
                    onMouseLeave={() => setHoveredResort(null)}
                  >
                    {resort.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResortNavigation;